import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../auth';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, User, Calendar, Clock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const POSITIONS = ['production', 'packing', 'delivery', 'manager', 'other'];
const POSITION_LABELS = {
  production: '🏭 Производство',
  packing: '📦 Упаковка',
  delivery: '🚚 Доставка',
  manager: '👔 Менеджер',
  other: '👤 Другое',
};

export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [empDialog, setEmpDialog] = useState(false);
  const [shiftDialog, setShiftDialog] = useState(false);
  const [empForm, setEmpForm] = useState({
    name: '', phone: '', position: 'production',
    salary_type: 'monthly', salary_amount: 0, notes: ''
  });
  const [shiftForm, setShiftForm] = useState({
    employee_id: '', shift_date: new Date().toISOString().split('T')[0],
    shift_type: 'full', hours_planned: 8, notes: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    const [emp, sh] = await Promise.all([
      axios.get(`${API}/employees`),
      axios.get(`${API}/shifts`),
    ]);
    setEmployees(emp.data);
    setShifts(sh.data);
  };

  const handleCreateEmp = async () => {
    try {
      await axios.post(`${API}/employees`, empForm);
      toast.success('Сотрудник добавлен');
      setEmpDialog(false);
      setEmpForm({ name: '', phone: '', position: 'production', salary_type: 'monthly', salary_amount: 0, notes: '' });
      fetchData();
    } catch (err) { toast.error('Ошибка'); }
  };

  const handleCreateShift = async () => {
    try {
      await axios.post(`${API}/shifts`, {
        ...shiftForm,
        shift_date: new Date(shiftForm.shift_date).toISOString()
      });
      toast.success('Смена добавлена');
      setShiftDialog(false);
      fetchData();
    } catch (err) { toast.error('Ошибка'); }
  };

  const handleDeleteEmp = async (id) => {
    if (!window.confirm('Уволить сотрудника?')) return;
    await axios.delete(`${API}/employees/${id}`);
    fetchData();
  };

  const handleUpdateShift = async (id, status) => {
    await axios.patch(`${API}/shifts/${id}?status=${status}`);
    fetchData();
  };

  const handleDeleteShift = async (id) => {
    await axios.delete(`${API}/shifts/${id}`);
    fetchData();
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('ru-RU');

  // Group shifts by date
  const shiftsByDate = {};
  shifts.forEach(s => {
    const day = s.shift_date.split('T')[0];
    if (!shiftsByDate[day]) shiftsByDate[day] = [];
    shiftsByDate[day].push(s);
  });

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Сотрудники и график</h1>
        <p className="text-gray-500 mt-1">Учёт сотрудников и расписание смен</p>
      </div>

      <Tabs defaultValue="employees">
        <TabsList>
          <TabsTrigger value="employees" data-testid="tab-employees">👥 Сотрудники ({employees.length})</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">📅 Расписание ({shifts.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="employees" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Dialog open={empDialog} onOpenChange={setEmpDialog}>
              <DialogTrigger asChild>
                <Button className="cff-button-primary" data-testid="add-emp-btn">
                  <Plus className="h-4 w-4 mr-2" /> Сотрудник
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Новый сотрудник</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                  <div>
                    <Label>Имя</Label>
                    <Input value={empForm.name} onChange={(e) => setEmpForm({...empForm, name: e.target.value})} data-testid="emp-name-input" />
                  </div>
                  <div>
                    <Label>Телефон</Label>
                    <Input value={empForm.phone} onChange={(e) => setEmpForm({...empForm, phone: e.target.value})} />
                  </div>
                  <div>
                    <Label>Должность</Label>
                    <Select value={empForm.position} onValueChange={(v) => setEmpForm({...empForm, position: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {POSITIONS.map(p => <SelectItem key={p} value={p}>{POSITION_LABELS[p]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Тип зарплаты</Label>
                      <Select value={empForm.salary_type} onValueChange={(v) => setEmpForm({...empForm, salary_type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Месячная</SelectItem>
                          <SelectItem value="hourly">Почасовая</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Сумма (₩)</Label>
                      <Input type="number" value={empForm.salary_amount} onChange={(e) => setEmpForm({...empForm, salary_amount: parseFloat(e.target.value) || 0})} />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEmpDialog(false)}>Отмена</Button>
                  <Button onClick={handleCreateEmp} className="cff-button-primary" data-testid="save-emp-btn">Добавить</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {employees.length === 0 ? (
              <Card className="p-12 text-center col-span-full">
                <User className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Сотрудников пока нет</p>
              </Card>
            ) : employees.map(e => (
              <Card key={e.id} className="p-5 cff-hover-lift" data-testid={`emp-card-${e.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="bg-orange-50 p-2 rounded-md">
                      <User className="h-4 w-4 text-[#556B2F]" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{e.name}</p>
                      <p className="text-xs text-gray-500">{POSITION_LABELS[e.position]}</p>
                    </div>
                  </div>
                  <button onClick={() => handleDeleteEmp(e.id)} className="text-red-500 hover:text-red-700">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="text-xs space-y-1">
                  <p className="text-gray-700">{e.phone || 'Нет телефона'}</p>
                  <p className="font-bold cff-text-numeric">
                    ₩{e.salary_amount.toLocaleString()} 
                    <span className="text-gray-500 font-normal">/{e.salary_type === 'monthly' ? 'мес' : 'час'}</span>
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="schedule" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Dialog open={shiftDialog} onOpenChange={setShiftDialog}>
              <DialogTrigger asChild>
                <Button className="cff-button-primary" data-testid="add-shift-btn">
                  <Plus className="h-4 w-4 mr-2" /> Смена
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Назначить смену</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                  <div>
                    <Label>Сотрудник</Label>
                    <select
                      value={shiftForm.employee_id}
                      onChange={(e) => setShiftForm({...shiftForm, employee_id: e.target.value})}
                      className="w-full mt-1 border border-gray-300 rounded-md h-10 px-3"
                      data-testid="shift-emp-select"
                    >
                      <option value="">Выберите...</option>
                      {employees.map(e => <option key={e.id} value={e.id}>{e.name} - {POSITION_LABELS[e.position]}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Дата</Label>
                    <Input type="date" value={shiftForm.shift_date} onChange={(e) => setShiftForm({...shiftForm, shift_date: e.target.value})} data-testid="shift-date-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Тип</Label>
                      <Select value={shiftForm.shift_type} onValueChange={(v) => setShiftForm({...shiftForm, shift_type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="morning">🌅 Утро</SelectItem>
                          <SelectItem value="afternoon">🌆 Вечер</SelectItem>
                          <SelectItem value="full">⏰ Полный день</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Часов</Label>
                      <Input type="number" value={shiftForm.hours_planned} onChange={(e) => setShiftForm({...shiftForm, hours_planned: parseFloat(e.target.value) || 0})} />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShiftDialog(false)}>Отмена</Button>
                  <Button onClick={handleCreateShift} className="cff-button-primary" data-testid="save-shift-btn">Назначить</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {Object.keys(shiftsByDate).length === 0 ? (
            <Card className="p-12 text-center">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Смен пока нет</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {Object.entries(shiftsByDate).sort().map(([date, dayShifts]) => (
                <Card key={date} className="p-4" data-testid={`shift-day-${date}`}>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-[#556B2F]" />
                    {formatDate(date)}
                    <span className="text-sm font-normal text-gray-500">({dayShifts.length})</span>
                  </h3>
                  <div className="space-y-2">
                    {dayShifts.map(s => (
                      <div key={s.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="bg-white p-1.5 rounded">
                            <User className="h-3 w-3" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{s.employee_name}</p>
                            <p className="text-xs text-gray-500">{POSITION_LABELS[s.employee_position]} · {s.hours_planned}ч</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            value={s.status}
                            onChange={(e) => handleUpdateShift(s.id, e.target.value)}
                            className="text-xs border border-gray-300 rounded px-2 py-1"
                            data-testid={`shift-status-${s.id}`}
                          >
                            <option value="scheduled">📋 Запланирована</option>
                            <option value="completed">✅ Выполнена</option>
                            <option value="absent">❌ Не пришёл</option>
                            <option value="sick">🤒 Больничный</option>
                          </select>
                          <button onClick={() => handleDeleteShift(s.id)} className="text-red-500 hover:text-red-700">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
